/* eslint-disable jsx-a11y/interactive-supports-focus */
import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Popper from '@material-ui/core/Popper';
import { Link, useLocation, useHistory } from 'react-router-dom';
import Hidden from '@material-ui/core/Hidden';
import ClassNames from 'classnames';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import useWindowSize from 'components/useWindowSize';
import useShortMenu from 'components/useShortMenu';
import ButtonBase from '@material-ui/core/ButtonBase';
import { isMobile } from 'react-device-detect';
import Blogs from './SubMenu/Blogs';
import News from './SubMenu/News';
import Assessments from './SubMenu/Assessments';
import NavBarContext from '../navBarContext';
import Topics from './SubMenu/Topics';
import AccountMenu from './AccountMenu';
import ClientResources from './SubMenu/ClientResources';
import ArrowDown from '../images/arrow-down.svg';
import ArrowUp from '../images/arrow-up.svg';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    borderTop: '1px solid #E3E3E3',
    alignItems: 'center',
  },
  embedded: {
    borderTop: 'none',
  },
  root: {
    height: 49,
    display: 'flex',
    justifyContent: 'flex-start',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  lists: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    justifyContent: 'space-between',
    height: '100%',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  mobileList: {
    flexDirection: 'column',
    padding: '30px 5.5%',
  },
  mobileListItem: {
    ...theme.typography.body2,
    fontSize: 17,
    margin: '5px 0',
    '& a': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  listItem: {
    ...theme.typography.body2,
    marginRight: 40,
    color: '#5C5C5C',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      marginRight: 10,
    },
  },
  link: {
    fontFamily: 'MadaBold',
    color: '#01619B !important',
    textDecoration: 'none',
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    width: 'auto',
    background: '#FFFFFF',
    borderRadius: 2,
    maxHeight: props => props.menuHeight || '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    boxShadow: '0 2px 4px rgba(0,0,0,0.20)',
    [theme.breakpoints.down('xs')]: {
      boxShadow: 'none',
      width: '100%',
      paddingBottom: '20%',
      display: 'block',
    },
    [theme.breakpoints.down('380')]: {
      paddingBottom: '25%',
    },
  },
  popover: {
    marginTop: 10,
    zIndex: 9999,
    [theme.breakpoints.down('xs')]: {
      marginTop: 9,
      width: '100%',
      transform: props =>
        `translate3d(0, ${props.popoverTopMargin}, 0) !important`,
    },
    '&[x-placement="bottom"]': {
      transform: props =>
        `translate3d(22.5%, ${props.popoverTopMargin}, 0) !important`,
      [theme.breakpoints.down('1500')]: {
        transform: props =>
          `translate3d(15%, ${props.popoverTopMargin}, 0) !important`,
      },
      [theme.breakpoints.down('1400')]: {
        transform: props =>
          `translate3d(5%, ${props.popoverTopMargin}, 0) !important`,
      },
      [theme.breakpoints.down('1200')]: {
        transform: props =>
          `translate3d(5px, ${props.popoverTopMargin}, 0) !important`,
      },
      [theme.breakpoints.down('1000')]: {
        transform: props =>
          `translate3d(0, ${props.popoverTopMargin}, 0) !important`,
      },
    },
  },
  mobilePopover: {
    marginTop: 0,
    minWidth: 250,
    [theme.breakpoints.down('xs')]: {
      transform: 'translate3d(0, 149px, 0) !important',
    },
  },
  activeItem: {
    color: '#01619B',
    borderTop: '1px solid #01619B',
    marginTop: '-1px',
  },
  moreBtn: {
    height: '100%',
  },
  activeMoreBtn: {
    borderTop: '1px solid #01619B',
    '& p': {
      color: '#01619B',
    },
  },
  cursor: {
    cursor: 'pointer',
  },
  embeddedRightContent: {
    display: 'flex',
  },
}));

function SubMenu({ menu }) {
  const {
    clientDetails,
    sendMixpanelEvent,
    clientResources,
    clientLanding,
    isEmbed,
  } = useContext(NavBarContext);
  const [openPopoverId, setOpenPopoverId] = useState();
  const [showMore, setShowMore] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const location = useLocation();
  const history = useHistory();
  const { width, height } = useWindowSize();
  const popoverTopMargin =
    isEmbed && width >= 600
      ? '75px'
      : isEmbed && width < 600
      ? '90px'
      : '140px';
  let isCovidCenter = _get(clientLanding, 'slug', '') === 'covid-19';
  const userTerm = _get(clientLanding, 'userTerm', '') || '';
  const hasClientDetails = !_isEmpty(clientDetails);
  const { shortMenu, hasClient } = useShortMenu();
  const isHomePage = window.location.pathname === '/';
  // short menu + homepage + noclient= 75 + 52
  // short menu + client  = 75;
  // !short menu + homepage = 149+ 52
  // !short menu + !homepage = 149
  const menuHeightOffset = shortMenu
    ? isHomePage && !hasClient
      ? 127
      : 52
    : isHomePage
    ? 201
    : 149;
  const classes = useStyles({
    menuHeight: height - menuHeightOffset,
    popoverTopMargin,
  });
  useEffect(() => {
    clearRef();
    isCovidCenter = _get(clientLanding, 'slug', '') === 'covid-19';
  }, [location, clientLanding]);
  const handleMouseEnter = (event, id, name, clicked) => {
    if (openPopoverId === id) {
      clearRef();
    } else {
      if (id !== 'home') {
        sendMixpanelEvent(`Menu ${clicked ? 'Click' : 'Hover'}`, {
          type: name,
        });
      }

      setAnchorEl(event.currentTarget);
      setOpenPopoverId(id);
      setShowMore(false);
    }
  };

  const clickHandler = id => {
    if (openPopoverId !== id) return;
    clearRef();
    history.push(getLink(id));
  };

  const clearRef = () => {
    setAnchorEl(null);
    setOpenPopoverId(null);
    setShowMore(false);
  };

  const handleSmalleMenuClick = name => {
    sendMixpanelEvent('Menu Click', {
      type: name,
    });
  };

  const toggleShowmore = event => {
    setOpenPopoverId(null);
    setAnchorEl(event.currentTarget);
    setShowMore(!showMore);
  };

  const content = slug => {
    const contentComponent = {
      blogs: <Blogs />,
      news: <News />,
      assessments: <Assessments />,
      topics: <Topics />,
      'client-exclusive': <ClientResources />,
    };

    return contentComponent[slug] || null;
  };

  const getLink = slug => {
    const links = {
      news: '/news',
      'covid-19': '/landing/covid-19',
      'client-exclusive': '/client-resources',
      'in-crisis': '/in-crisis',
      assessments: '/assessments',
      'how-it-works': '/how-it-works',
      home: '/',
      topics: '/topics',
      blogs: '/blogs',
    };

    return links[slug];
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;
  const clonedMenu = [...menu];
  const ACSplice =
    width <= 413
      ? 2
      : width <= 729
      ? 3
      : width < 970 && hasClientDetails
      ? 4
      : width < 1060 && hasClientDetails
      ? 5
      : 7;
  const mobileMenu = clonedMenu.splice(0, ACSplice);
  return (
    <div
      className={ClassNames(classes.container, {
        [classes.embedded]: isEmbed,
      })}
      data-test-id="sub-menu"
    >
      <Hidden mdDown>
        <div className={classes.root} onMouseLeave={clearRef}>
          <ul className={classes.lists}>
            {menu.map(({ slug, name }) => {
              const hasNoPopover = [
                'covid-19',
                'how-it-works',
                'in-crisis',
              ].includes(slug);
              if (
                slug === 'client-exclusive' &&
                (_isEmpty(clientDetails) ||
                  _isEmpty(clientResources) ||
                  isCovidCenter)
              )
                return null;
              return (
                <li
                  key={slug}
                  id={id}
                  className={ClassNames(classes.listItem, {
                    [classes.activeItem]: openPopoverId === slug,
                  })}
                >
                  {hasNoPopover ? (
                    <Link
                      to={getLink(slug)}
                      className={classes.link}
                      onClick={e => handleMouseEnter(e, slug, name, true)}
                      onMouseEnter={clearRef}
                    >
                      {name}
                    </Link>
                  ) : (
                    <>
                      <Typography
                        onMouseEnter={e => handleMouseEnter(e, slug, name)}
                        onClick={() => clickHandler(slug)}
                        className={openPopoverId === slug ? classes.cursor : ''}
                        data-test-id={`${name.toLowerCase()}-menu`}
                      >
                        {slug === 'client-exclusive'
                          ? `${userTerm} Resources`
                          : name}
                      </Typography>

                      <Popper
                        open={openPopoverId === slug}
                        anchorEl={anchorEl}
                        className={classes.popover}
                        placement={
                          ['blogs', 'news'].includes(slug)
                            ? 'bottom'
                            : 'bottom-start'
                        }
                        disablePortal
                        modifiers={{
                          flip: {
                            enabled: false,
                          },
                          preventOverflow: {
                            enabled: true,
                            boundariesElement: 'window',
                          },
                        }}
                      >
                        <div className={classes.paper}>{content(slug)}</div>
                      </Popper>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </Hidden>
      <Hidden lgUp>
        <div className={classes.root} onMouseLeave={clearRef}>
          <ul className={classes.lists}>
            {mobileMenu.map(({ slug, name }) => {
              const hasNoPopover = [
                'covid-19',
                'how-it-works',
                'in-crisis',
              ].includes(slug);
              if (
                slug === 'client-exclusive' &&
                (_isEmpty(clientDetails) ||
                  _isEmpty(clientResources) ||
                  isCovidCenter)
              )
                return null;
              return (
                <li
                  key={slug}
                  id={id}
                  className={ClassNames(classes.listItem, {
                    [classes.activeItem]: openPopoverId === slug,
                  })}
                >
                  {hasNoPopover ? (
                    <Link
                      to={getLink(slug)}
                      className={classes.link}
                      onClick={e => handleMouseEnter(e, slug, name, true)}
                    >
                      {name}
                    </Link>
                  ) : (
                    <>
                      {isMobile ? (
                        <Typography
                          onClick={e => handleMouseEnter(e, slug, name, true)}
                        >
                          {slug === 'client-exclusive'
                            ? `${userTerm} Resources`
                            : name}
                        </Typography>
                      ) : (
                        <Typography
                          onMouseEnter={e => handleMouseEnter(e, slug, name)}
                          onClick={() => clickHandler(slug)}
                        >
                          {slug === 'client-exclusive'
                            ? `${userTerm} Resources`
                            : name}
                        </Typography>
                      )}

                      <Popper
                        open={openPopoverId === slug}
                        anchorEl={anchorEl}
                        className={classes.popover}
                        placement={
                          ['blogs', 'news'].includes(slug)
                            ? 'bottom'
                            : 'bottom-start'
                        }
                        disablePortal
                        modifiers={{
                          flip: {
                            enabled: false,
                          },
                          preventOverflow: {
                            enabled: true,
                            boundariesElement: 'window',
                          },
                        }}
                      >
                        <div className={classes.paper}>{content(slug)}</div>
                      </Popper>
                    </>
                  )}
                </li>
              );
            })}
            {clonedMenu.length > 1 && (
              <>
                <ButtonBase
                  disableTouchRipple
                  onClick={toggleShowmore}
                  className={ClassNames(classes.moreBtn, {
                    [classes.activeMoreBtn]: showMore,
                  })}
                >
                  <Typography color="textPrimary">More</Typography>
                  <img src={showMore ? ArrowUp : ArrowDown} alt="" />
                </ButtonBase>

                <Popper
                  open={showMore}
                  anchorEl={anchorEl}
                  className={ClassNames(classes.popover, classes.mobilePopover)}
                  placement="bottom-start"
                  disablePortal
                  modifiers={{
                    flip: {
                      enabled: false,
                    },
                    preventOverflow: {
                      enabled: true,
                      boundariesElement: 'window',
                    },
                  }}
                >
                  <div className={classes.paper}>
                    <ul
                      className={ClassNames(classes.lists, classes.mobileList)}
                    >
                      {clonedMenu
                        .filter(({ slug }) => slug !== 'home')
                        .map(({ slug, name }) => {
                          if (
                            slug === 'client-exclusive' &&
                            (_isEmpty(clientDetails) ||
                              _isEmpty(clientResources) ||
                              isCovidCenter)
                          )
                            return null;

                          return (
                            <li className={classes.mobileListItem} key={slug}>
                              <Link
                                to={getLink(slug)}
                                className={ClassNames(classes.link)}
                                onClick={() => handleSmalleMenuClick(name)}
                              >
                                {slug === 'client-exclusive'
                                  ? `${userTerm} Resources`
                                  : name}
                              </Link>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                </Popper>
              </>
            )}
          </ul>
        </div>
      </Hidden>
      <Hidden xsDown>
        <div className={isEmbed ? classes.embeddedRightContent : ''}>
          <AccountMenu />
        </div>
      </Hidden>
    </div>
  );
}

SubMenu.propTypes = {
  menu: PropTypes.array,
};
export default React.memo(SubMenu);
